import { UTCDate } from "./utc-date";
import moment from "moment";
import "moment-timezone";
import { GermanTimezone, UtcTimezone } from "./date.constants";
import { getShortTimeZoneName, getFullTimeZoneName, getFormattedTimeZoneOffset } from "./timezone.formatter";
import type { BerlinDate } from "./berlin-date";

type Property<T> = keyof T & string;

export class DateTime {
  readonly offsetMinutes: number; // Minutes added to UTC to get the original local wall time
  readonly zoneId?: string; // IANA time zone ID (e.g. "Europe/Berlin")
  private readonly epochMillis: number; // UTC epoch millis

  private constructor(epochMillis?: number, offsetMinutes?: number, zoneId?: string) {
    // special logic to allow class-transformer to initialize the date
    if (arguments.length === 0) {
      this.epochMillis = 0;
      this.offsetMinutes = 0;
      this.zoneId = undefined;
      return;
    }
    this.epochMillis = epochMillis;
    this.offsetMinutes = offsetMinutes ?? 0.0;
    this.zoneId = zoneId;
    Object.freeze(this); // enforce immutability
  }

  static DateTimeIsoPart<T>(propertyName: Property<T>, documentAlias = "c"): string {
    return `SUBSTRING(${documentAlias}.${propertyName}, 0, 24)`;
  }

  /**
   * Extracts the UTC ISO part from a raw Cosmos DB expression that may contain
   * a serialized DateTime value (`UTC_ISO|Temporal`).
   * Safe for both UTCDate (`YYYY-MM-DD`) and DateTime values.
   */
  static DateTimeIsoPartExpr(cosmosExpression: string): string {
    return `SUBSTRING(${cosmosExpression}, 0, 24)`;
  }

  /**
   * Like {@link DateTimeIsoPartExpr}, but always a complete ISO 8601 date-time: a date-only value (`UTCDate`,
   * `YYYY-MM-DD`) is padded to midnight UTC, because Cosmos `DateTimeDiff` returns undefined for it and the row
   * then silently drops out of the filter. The time part is cut off, so only use it with day granularity.
   */
  static DateTimeIsoDayExpr(cosmosExpression: string): string {
    return `CONCAT(SUBSTRING(${cosmosExpression}, 0, 10), "T00:00:00Z")`;
  }

  /**
   * Current instant as DateTime.
   * @param zoneId Optional IANA zone. If provided the correct offset for that zone at this instant is calculated.
   */
  // Factory: current instant
  static now(zoneId?: string): DateTime {
    if (typeof window !== "undefined") {
      zoneId = Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    zoneId ??= UtcTimezone;

    const epoch = Date.now();
    const baseDate = new Date(epoch);
    const offset = zoneId ? getOffsetMinutesForZone(baseDate, zoneId) : -baseDate.getTimezoneOffset();
    return new DateTime(epoch, offset, zoneId);
  }

  /**
   * Construct from native Date, interpreting its local components in the specified timezone.
   *
   * When a zoneId is provided, the Date's local components (year, month, day, hour, etc.)
   * are re-interpreted as being in that timezone, not in the system's local timezone.
   * This effectively "moves" the wall-clock time from the local timezone to the target timezone.
   *
   * Without zoneId, the Date is interpreted in the system's local timezone.
   *
   * @param date - A native JavaScript Date object
   * @param zoneId - Optional IANA timezone identifier (e.g., "Europe/Berlin", "America/New_York")
   * @returns A new DateTime instance
   *
   * @example
   * // System timezone is Europe/Berlin (UTC+1 in winter)
   * // Create a Date representing 12:00 local time
   * const date = new Date(2025, 0, 15, 12, 0, 0); // Jan 15, 2025 12:00:00 local
   *
   * // Interpret as Berlin time (same as local in this example)
   * const dtBerlin = DateTime.fromDate(date, "Europe/Berlin");
   * // dtBerlin represents 12:00:00 Berlin time = 11:00:00 UTC
   * // dtBerlin.offsetMinutes = 60
   *
   * @example
   * // System timezone is Europe/Berlin (UTC+1 in winter)
   * const date = new Date(2025, 0, 15, 12, 0, 0); // Jan 15, 2025 12:00:00 local (Berlin)
   *
   * // Interpret as New York time (UTC-5 in winter)
   * const dtNY = DateTime.fromDate(date, "America/New_York");
   * // The 12:00 is now interpreted as New York time, not Berlin time
   * // dtNY represents 12:00:00 New York time = 17:00:00 UTC
   * // dtNY.offsetMinutes = -300
   *
   * @example
   * // Without zoneId - uses system timezone
   * const date = new Date(2025, 0, 15, 12, 0, 0);
   * const dt = DateTime.fromDate(date);
   * // dt represents 12:00:00 in the system's local timezone
   * // dt.zoneId is undefined
   *
   * @example
   * // Non-hour offset: Asia/Kolkata (UTC+5:30)
   * const date = new Date(2025, 0, 15, 12, 0, 0); // 12:00 local
   * const dt = DateTime.fromDate(date, "Asia/Kolkata");
   * // dt represents 12:00:00 Kolkata time = 06:30:00 UTC
   * // dt.offsetMinutes = 330
   *
   * @example
   * // Negative non-hour offset: America/St_Johns (UTC-3:30 in winter)
   * const date = new Date(2025, 0, 15, 12, 0, 0); // 12:00 local
   * const dt = DateTime.fromDate(date, "America/St_Johns");
   * // dt represents 12:00:00 St. John's time = 15:30:00 UTC
   * // dt.offsetMinutes = -210
   */
  static fromDate(date: Date, zoneId?: string): DateTime {
    const localOffsetMillis = -date.getTimezoneOffset() * 60_000;
    const zoneOffsetMillis = zoneId ? getOffsetMinutesForZone(date, zoneId) * 60_000 : localOffsetMillis;

    return new DateTime(date.getTime() + (localOffsetMillis - zoneOffsetMillis), zoneOffsetMillis / 60_000, zoneId);
  }

  /**
   * Parse extended ISO-like string: YYYY-MM-DDTHH:mm[:ss][.fraction](Z|±HH:MM)[zone].
   * Example: 2025-06-15T12:00:00.123+02:00[Europe/Berlin]
   * Zone suffix is optional; if absent only the numeric offset is retained.
   */
  // Parse ISO-like string
  static parse(input: string): DateTime {
    if (!input) return null;

    const regex =
      /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?(Z|[+-]\d{2}:\d{2})(?:\[([^\]]+)])?$/;
    const match = regex.exec(input);
    if (!match) throw new Error(`Invalid DateTime format: ${input}`);
    const [, y, M, d, h, m, s = "00", frac = "0", offsetStr, zoneId] = match;
    const year = Number(y);
    const month = Number(M);
    const day = Number(d);
    const hour = Number(h);
    const minute = Number(m);
    const second = Number(s);
    const ms = parseFractionToMillis(frac);
    const offsetMinutes = parseOffsetToMinutes(offsetStr);
    const utcMillis = Date.UTC(year, month - 1, day, hour, minute, second, ms) - offsetMinutes * 60000;
    return new DateTime(utcMillis, offsetMinutes, zoneId || (offsetMinutes === 0 ? "UTC" : undefined));
  }

  /**
   * Serialization helper delegating to toJSON (persistence format).
   * Format: UTC ISO instant + Temporal ZonedDateTime-like representation
   * Example: `2025-01-15T11:00:00.000Z|2025-01-15T12:00:00.000+01:00[Europe/Berlin]`
   *
   * The first part is the UTC instant, the second part follows the Temporal API
   * ZonedDateTime format with local time, offset, and optional zone ID.
   */
  static serialize(input: DateTime): string {
    const utcIso = new Date(input.toEpochMillis()).toISOString();
    const temporalZoned = input.toTemporalZonedString();
    return `${utcIso}|${temporalZoned}`;
  }

  /**
   * Symmetric deserialize alias for persistence format.
   * Supports both legacy format (`UTC|offset|zoneId`) and new Temporal-like format
   * (`UTC|zonedDateTime`).
   */
  static deserialize(s: string): DateTime {
    const parts = s.split("|");

    // New Temporal-like format: UTC|zonedDateTime
    if (parts.length === 2) {
      const [utcIso, temporalZoned] = parts;
      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(utcIso))
        throw new Error("Invalid UTC segment in persisted DateTime");
      // Parse the Temporal ZonedDateTime-like string
      return DateTime.parse(temporalZoned);
    }

    // Legacy format: UTC|offset|zoneId (for backward compatibility)
    if (parts.length === 3) {
      const [utcIso, offsetStr, zoneId] = parts;
      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(utcIso))
        throw new Error("Invalid UTC segment in persisted DateTime");
      const epochMillis = Date.parse(utcIso);
      const offsetMinutes = parseOffsetToMinutes(offsetStr);
      return DateTime.fromEpoch(epochMillis, offsetMinutes, zoneId);
    }

    throw new Error("Invalid persisted DateTime string");
  }

  /**
   * Build from local wall time components + offset (or derive from zoneId).
   * month is 0-based (like Date.UTC / getUTCMonth).
   * If only zoneId is provided offsetMinutes is derived from midnight of that date in the zone.
   */
  static fromLocalParts(params: {
    year: number;
    monthIndex: number;
    day: number;
    hour?: number;
    minute?: number;
    second?: number;
    millisecond?: number;
    offsetMinutes?: number;
    zoneId?: string;
  }): DateTime {
    const { year, monthIndex, day, hour = 0, minute = 0, second = 0, millisecond = 0, offsetMinutes } = params;
    let zoneId: string = params.zoneId;
    let effectiveOffset = offsetMinutes;
    if (effectiveOffset === undefined) {
      if (!zoneId && typeof window === "undefined") {
        throw new Error("Either offsetMinutes or zoneId must be provided");
      }
      zoneId ??= Intl.DateTimeFormat().resolvedOptions().timeZone;
      effectiveOffset = getOffsetMinutesForZoneAtMidnight(year, monthIndex, day, zoneId);
    }

    const utcMillis = Date.UTC(year, monthIndex, day, hour, minute, second, millisecond) - effectiveOffset * 60000;
    return new DateTime(utcMillis, effectiveOffset, zoneId);
  }

  /** Direct build from UTC epoch milliseconds + offset + optional zone */
  static fromEpoch(epochMillis: number, offsetMinutes: number, zoneId?: string): DateTime {
    return new DateTime(epochMillis, offsetMinutes, zoneId);
  }

  /** Alias of fromEpoch for symmetry */
  static fromEpochMillis(epochMillis: number, offsetMinutes: number, zoneId?: string): DateTime {
    return this.fromEpoch(epochMillis, offsetMinutes, zoneId);
  }

  /**
   * Convert a date-only UTCDate to local midnight DateTime for the German time zone (DST aware).
   */
  static fromUTCToGermanDate(utcDate: UTCDate): DateTime {
    return DateTime.fromLocalParts({
      year: utcDate.getFullYear(),
      monthIndex: utcDate.getMonth(),
      day: utcDate.getDate(),
      offsetMinutes: getOffsetMinutesForZoneAtMidnight(
        utcDate.getFullYear(),
        utcDate.getMonth(),
        utcDate.getDate(),
        GermanTimezone
      ),
      zoneId: GermanTimezone,
    });
  }

  /**
   * Create DateTime from BerlinDate at midnight in German timezone.
   * The Berlin date is converted to a DateTime representing midnight (00:00:00) in the Europe/Berlin timezone.
   * Example: BerlinDate(2025-01-15) -> DateTime(2025-01-15T00:00:00+01:00)
   */
  static fromBerlinDateToGermanDateAtMidnight(berlinDate: BerlinDate): DateTime {
    return DateTime.fromLocalParts({
      year: berlinDate.getFullYear(),
      monthIndex: berlinDate.getMonth(),
      day: berlinDate.getDate(),
      offsetMinutes: getOffsetMinutesForZoneAtMidnight(
        berlinDate.getFullYear(),
        berlinDate.getMonth(),
        berlinDate.getDate(),
        GermanTimezone
      ),
      zoneId: GermanTimezone,
    });
  }

  static fromUTCToDateTimeInZone(utcDate: UTCDate, zoneId: string): DateTime {
    return DateTime.fromLocalParts({
      year: utcDate.getFullYear(),
      monthIndex: utcDate.getMonth(),
      day: utcDate.getDate(),
      offsetMinutes: getOffsetMinutesForZoneAtMidnight(
        utcDate.getFullYear(),
        utcDate.getMonth(),
        utcDate.getDate(),
        zoneId
      ),
      zoneId: zoneId,
    });
  }

  /**
   * Convert UTCDate to midnight in the current browser time zone. Browser only.
   */
  static fromUTCToLocalDateAtMidnight(utcDate: UTCDate): DateTime {
    if (typeof window === "undefined")
      throw new Error("fromUTCToLocalDateAtMidnight can only be used in a browser environment");

    const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return DateTime.fromLocalParts({
      year: utcDate.getFullYear(),
      monthIndex: utcDate.getMonth(),
      day: utcDate.getDate(),
      offsetMinutes: getOffsetMinutesForZoneAtMidnight(
        utcDate.getFullYear(),
        utcDate.getMonth(),
        utcDate.getDate(),
        localZone
      ),
      zoneId: localZone,
    });
  }

  /** Epoch 0 with offset 0 */
  static zero(): DateTime {
    return new DateTime(0, 0, undefined);
  }

  /** Same instant, new zone (recomputes offset) */
  withZone(zoneId: string): DateTime {
    const offset = getOffsetMinutesForZone(this.toDate(), zoneId);
    return new DateTime(this.epochMillis, offset, zoneId);
  }

  /** Same instant, explicit different offset (zone unchanged) */
  withOffset(offsetMinutes: number): DateTime {
    return new DateTime(this.epochMillis, offsetMinutes, this.zoneId);
  }

  /** ISO string in UTC (no offset / zone info) */
  toISOString(): string {
    return new Date(this.epochMillis).toISOString();
  }

  /** Extended ISO with numeric offset and optional zone */
  toExtendedISOString(): string {
    const { year, monthIndex, day, hour, minute, second, millisecond: ms } = this.getLocalComponents();
    const base = `${pad(year, 4)}-${pad(monthIndex + 1)}-${pad(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}${formatFraction(ms)}`;
    const offset = formatOffset(this.offsetMinutes);
    return this.zoneId ? `${base}${offset}[${this.zoneId}]` : `${base}${offset}`;
  }

  /**
   * Returns a Temporal ZonedDateTime-like string representation.
   * Format: YYYY-MM-DDTHH:mm:ss.sss±HH:MM[ZoneId]
   * Example: 2025-01-15T12:00:00.000+01:00[Europe/Berlin]
   *
   * This format is designed to be compatible with the future Temporal API's
   * ZonedDateTime.toString() format.
   */
  toTemporalZonedString(): string {
    const { year, monthIndex, day, hour, minute, second, millisecond: ms } = this.getLocalComponents();
    const base = `${pad(year, 4)}-${pad(monthIndex + 1)}-${pad(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}${formatFraction(ms)}`;
    const offset = formatOffset(this.offsetMinutes);
    return this.zoneId ? `${base}${offset}[${this.zoneId}]` : `${base}${offset}`;
  }

  /** Persistence format (serializeDateTime) */
  toJSON(): string {
    return DateTime.serialize(this);
  }

  /** Local wall-time components (after applying offset) */
  getLocalComponents(): {
    year: number;
    monthIndex: number;
    day: number;
    hour: number;
    minute: number;
    second: number;
    millisecond: number;
  } {
    const localMillis = this.epochMillis + this.offsetMinutes * 60000;
    const d = new Date(localMillis);
    return {
      year: d.getUTCFullYear(),
      monthIndex: d.getUTCMonth(),
      day: d.getUTCDate(),
      hour: d.getUTCHours(),
      minute: d.getUTCMinutes(),
      second: d.getUTCSeconds(),
      millisecond: d.getUTCMilliseconds(),
    };
  }

  /** Milliseconds since Unix epoch (UTC) */
  toEpochMillis(): number {
    return this.epochMillis;
  }

  /** Alias for toEpochMillis (Date interop) */
  getTime(): number {
    return this.epochMillis;
  }

  /** Native Date (UTC instant) */
  toDate(): Date {
    return new Date(this.epochMillis);
  }

  /**
   * ⚠️ **WARNING: This method is intended ONLY for UI date picker components.**
   *
   * Converts this DateTime back to a native Date object where the Date's local
   * components (year, month, day, hour, etc.) match the wall-clock time in
   * the DateTime's timezone.
   *
   * This is the **inverse operation** of `DateTime.fromDate(date, zoneId)`.
   *
   * ### ❌ DO NOT USE for:
   * - Persisting dates to database or API
   * - Comparing dates across timezones
   * - Date arithmetic or calculations
   * - Backend/API communication
   * - Any logic outside of UI date picker binding
   *
   * ### Why this method exists:
   * UI date pickers (like PrimeNG's p-calendar) work with native Date objects
   * and interpret their local components. When binding a DateTime to such
   * components, we need a Date whose local getHours(), getMinutes(), etc.
   * match the DateTime's wall-clock time - regardless of the system timezone.
   *
   * ### ⚠️ Important:
   * The returned Date's epoch (getTime()) will **NOT** match this DateTime's
   * epoch unless the system timezone happens to match the DateTime's timezone.
   *
   * @returns A native Date with local components matching this DateTime's wall-clock time
   *
   * @example
   * // DateTime representing 12:00:00 Berlin time (UTC+1)
   * const dt = DateTime.parse("2025-01-15T12:00:00+01:00[Europe/Berlin]");
   *
   * // System timezone is America/New_York (UTC-5)
   * const date = dt.toDateForUIComponentsOnly_UNSAFE();
   *
   * // date.getHours() === 12 (matches Berlin wall-clock time)
   * // date.getTime() !== dt.toEpochMillis() (epochs differ!)
   *
   * @example
   * // Roundtrip with fromDate preserves local components
   * const original = new Date(2025, 0, 15, 12, 30, 45);
   * const dt = DateTime.fromDate(original, "Europe/Berlin");
   * const restored = dt.toDateForUIComponentsOnly_UNSAFE();
   *
   * // restored has same local components as original:
   * // restored.getFullYear() === 2025
   * // restored.getMonth() === 0
   * // restored.getDate() === 15
   * // restored.getHours() === 12
   * // restored.getMinutes() === 30
   * // restored.getSeconds() === 45
   */
  toDateForUIComponentsOnly_UNSAFE(): Date {
    const { year, monthIndex, day, hour, minute, second, millisecond } = this.getLocalComponents();
    return new Date(year, monthIndex, day, hour, minute, second, millisecond);
  }

  /**
   * Converts this DateTime to a UTCDate, using the local time in the specified time zone.
   * @param zoneId IANA time zone ID (e.g. "Europe/Berlin") to calculate the local time.
   * @returns UTCDate object with year, month, and day according to the local time in the given time zone.
   */
  toUTCDate(zoneId: string): UTCDate {
    const { year, monthIndex, day } = this.withZone(zoneId).getLocalComponents();
    return UTCDate.fromYMD(year, monthIndex, day);
  }

  /** Instant equality */
  isEqual(other: DateTime): boolean {
    return this.epochMillis === other.epochMillis;
  }

  /** Is this instant before the other? */
  isBefore(other: DateTime): boolean {
    return this.epochMillis < other.epochMillis;
  }

  /** Is this instant after the other? */
  isAfter(other: DateTime): boolean {
    return this.epochMillis > other.epochMillis;
  }

  /** Minimum DateTime (at least one arg) */
  static min(...values: DateTime[]): DateTime {
    if (values.length === 0) return undefined;
    return values.reduce((m, d) => (d.toEpochMillis() < m.toEpochMillis() ? d : m));
  }

  /** Maximum DateTime (at least one arg) */
  static max(...values: DateTime[]): DateTime {
    if (values.length === 0) return undefined;
    return values.reduce((m, d) => (d.toEpochMillis() > m.toEpochMillis() ? d : m));
  }

  /** Compare two DateTime values: <0 if a<b, 0 if equal, >0 if a>b */
  static compare(a: DateTime, b: DateTime): number {
    return a.toEpochMillis() - b.toEpochMillis();
  }

  /** Primitive numeric coercion -> epochMillis */
  valueOf(): number {
    return this.epochMillis;
  }

  [Symbol.toPrimitive](): number {
    return this.epochMillis;
  }

  /** Local wall-time year (offset applied) */
  getYear(): number {
    return this.getLocalComponents().year;
  }

  /** Local wall-time month index (0-11) */
  getMonthIndex(): number {
    return this.getLocalComponents().monthIndex;
  }

  /** Local wall-time day of month (1-31) */
  getDayOfMonth(): number {
    return this.getLocalComponents().day;
  }

  /** Local wall-time hour (0-23) */
  getHour(): number {
    return this.getLocalComponents().hour;
  }

  /** Local wall-time minute (0-59) */
  getMinute(): number {
    return this.getLocalComponents().minute;
  }

  /** Local wall-time second (0-59) */
  getSecond(): number {
    return this.getLocalComponents().second;
  }

  /** Local wall-time millisecond (0-999) */
  getMillisecond(): number {
    return this.getLocalComponents().millisecond;
  }

  getShortTimeZoneName(locale: string): string {
    const date = new Date(this.epochMillis);
    return getShortTimeZoneName(date, locale, this.zoneId || UtcTimezone);
  }

  getFullTimeZoneName(locale: string): string {
    const date = new Date(this.epochMillis);
    return getFullTimeZoneName(date, locale, this.zoneId || UtcTimezone);
  }

  getFormattedTimeZoneOffset(locale: string): string {
    const date = new Date(this.epochMillis);
    return getFormattedTimeZoneOffset(date, locale, this.zoneId || UtcTimezone);
  }

  /** Add milliseconds (base primitive) */
  addMillis(delta: number): DateTime {
    if (!Number.isFinite(delta)) throw new Error("Invalid delta");
    const newEpoch = this.epochMillis + delta;
    let newOffset = this.offsetMinutes;
    if (this.zoneId) {
      newOffset = getOffsetMinutesForZone(new Date(newEpoch), this.zoneId);
    }
    return new DateTime(newEpoch, newOffset, this.zoneId);
  }

  /** Add whole days (24h blocks, UTC based). DST handled by recalculating offset if zoneId present. */
  addDays(days: number): DateTime {
    return this.addMillis(days * 86_400_000);
  }

  plusDays(days: number): DateTime {
    return this.addDays(days);
  }

  minusDays(days: number): DateTime {
    return this.addDays(-days);
  }

  plusHours(hours: number): DateTime {
    return this.addMillis(hours * 3_600_000);
  }

  minusHours(hours: number): DateTime {
    return this.plusHours(-hours);
  }

  plusMinutes(minutes: number): DateTime {
    return this.addMillis(minutes * 60_000);
  }

  minusMinutes(minutes: number): DateTime {
    return this.plusMinutes(-minutes);
  }

  plusSeconds(seconds: number): DateTime {
    return this.addMillis(seconds * 1_000);
  }

  minusSeconds(seconds: number): DateTime {
    return this.plusSeconds(-seconds);
  }

  plusMillis(ms: number): DateTime {
    return this.addMillis(ms);
  }

  minusMillis(ms: number): DateTime {
    return this.addMillis(-ms);
  }

  /**
   * Returns a human-readable string representation of this DateTime.
   * Format: "YYYY-MM-DD HH:mm:ss (TimeZone)" or "YYYY-MM-DD HH:mm:ss (±HH:MM)" if no zoneId.
   *
   * @example
   * // With zoneId
   * DateTime.parse("2025-01-15T12:30:45+01:00[Europe/Berlin]").toString()
   * // => "2025-01-15 12:30:45 (Europe/Berlin)"
   *
   * @example
   * // Without zoneId (UTC)
   * DateTime.parse("2025-01-15T12:30:45Z").toString()
   * // => "2025-01-15 12:30:45 (UTC)"
   *
   * @example
   * // Without zoneId (with offset)
   * DateTime.parse("2025-01-15T12:30:45+05:30").toString()
   * // => "2025-01-15 12:30:45 (+05:30)"
   */
  toString(): string {
    const { year, monthIndex, day, hour, minute, second } = this.getLocalComponents();
    const dateStr = `${pad(year, 4)}-${pad(monthIndex + 1)}-${pad(day)}`;
    const timeStr = `${pad(hour)}:${pad(minute)}:${pad(second)}`;
    const zoneStr = this.zoneId ?? (this.offsetMinutes === 0 ? "UTC" : formatOffset(this.offsetMinutes));
    return `${dateStr} ${timeStr} (${zoneStr})`;
  }
}

/** Type guard for DateTime */
export function isDateTime(v: unknown): v is DateTime {
  return !!v && typeof v === "object" && (v as any).constructor === DateTime;
}

/**
 * Calculates the UTC offset in minutes for a given date in a specific IANA time zone.
 *
 * The offset represents the number of minutes to ADD to UTC to get the local wall time.
 * - Positive offset: Zone is ahead of UTC (east of Prime Meridian)
 * - Negative offset: Zone is behind UTC (west of Prime Meridian)
 *
 * @param date - The Date object representing the instant in time
 * @param zoneId - IANA time zone identifier (e.g., "Europe/Berlin", "America/New_York")
 * @returns Offset in minutes from UTC
 *
 * @example
 * // Positive offset: Europe/Berlin (UTC+1 in winter, UTC+2 in summer)
 * const winterDate = new Date("2025-01-15T12:00:00Z");
 * getOffsetMinutesForZone(winterDate, "Europe/Berlin");
 * // Returns: 60 (UTC+01:00 → add 60 minutes to UTC)
 *
 * const summerDate = new Date("2025-07-15T12:00:00Z");
 * getOffsetMinutesForZone(summerDate, "Europe/Berlin");
 * // Returns: 120 (UTC+02:00 → add 120 minutes to UTC, DST active)
 *
 * @example
 * // Negative offset: America/New_York (UTC-5 in winter, UTC-4 in summer)
 * const winterDate = new Date("2025-01-15T12:00:00Z");
 * getOffsetMinutesForZone(winterDate, "America/New_York");
 * // Returns: -300 (UTC-05:00 → subtract 300 minutes from UTC)
 *
 * const summerDate = new Date("2025-07-15T12:00:00Z");
 * getOffsetMinutesForZone(summerDate, "America/New_York");
 * // Returns: -240 (UTC-04:00 → subtract 240 minutes from UTC, DST active)
 *
 * @example
 * // Zero offset: UTC
 * const date = new Date("2025-06-15T12:00:00Z");
 * getOffsetMinutesForZone(date, "UTC");
 * // Returns: 0
 *
 * @example
 * // Non-hour offset: Asia/Kolkata (UTC+05:30)
 * const date = new Date("2025-06-15T12:00:00Z");
 * getOffsetMinutesForZone(date, "Asia/Kolkata");
 * // Returns: 330 (5 hours × 60 + 30 minutes)
 *
 * @example
 * // Negative non-hour offset: America/St_Johns (UTC-03:30 in winter)
 * const winterDate = new Date("2025-01-15T12:00:00Z");
 * getOffsetMinutesForZone(winterDate, "America/St_Johns");
 * // Returns: -210 (-3 hours × 60 - 30 minutes)
 */
function getOffsetMinutesForZone(date: Date, zoneId: string): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: zoneId,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = dtf.formatToParts(date);
  const map: Record<string, string> = {};
  for (const p of parts) if (p.type !== "literal") map[p.type] = p.value;
  const year = Number(map.year);
  const month = Number(map.month);
  const day = Number(map.day);
  const hour = Number(map.hour);
  const minute = Number(map.minute);
  const second = Number(map.second);
  const utcConstructed = Date.UTC(year, month - 1, day, hour, minute, second);
  // `Intl.DateTimeFormat` truncates milliseconds, so a zone whose true offset is exactly zero (UTC) can see
  // utcConstructed trail `date` by its dropped millisecond remainder -- a tiny negative fraction that
  // Math.round resolves to -0 rather than 0. `|| 0` is falsy-safe here: it only replaces 0/-0, never a
  // genuine non-zero offset.
  return Math.round((utcConstructed - date.getTime()) / 60000) || 0;
}

export function getOffsetMinutesForZoneAtMidnight(year: number, month: number, date: number, zoneId: string): number {
  const midnight = moment.tz({ year, month, date, hour: 0, minute: 0, second: 0 }, zoneId).toDate();
  return getOffsetMinutesForZone(midnight, zoneId);
}

// Helper: parse offset like +02:30 or Z
function parseOffsetToMinutes(s: string): number {
  if (s === "Z") return 0;
  const m = /^([+-])(\d{2}):(\d{2})$/.exec(s);
  if (!m) throw new Error(`Invalid offset: ${s}`);
  const sign = m[1] === "+" ? 1 : -1;
  const hh = Number(m[2]);
  const mm = Number(m[3]);
  return sign * (hh * 60 + mm);
}

// Helper: format offset minutes as +HH:MM / -HH:MM / Z
function formatOffset(offsetMinutes: number): string {
  if (offsetMinutes === 0) return "Z";
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const abs = Math.abs(offsetMinutes);
  const hh = Math.floor(abs / 60);
  const mm = abs % 60;
  return `${sign}${pad(hh)}:${pad(mm)}`;
}

// Helper: fractional seconds -> milliseconds (truncate / pad to 3)
function parseFractionToMillis(f: string): number {
  if (!f || f === "0") return 0;
  return Number((f + "000").slice(0, 3));
}

function formatFraction(ms: number): string {
  if (ms === 0) return "";
  return `.${pad(ms, 3)}`;
}

function pad(n: number, width = 2): string {
  const s = String(Math.abs(n));
  return s.length >= width ? s : "0".repeat(width - s.length) + s;
}
